<template>
  <ul class="link-list">
    <li v-for="file in files" :key="file.id">
      <template v-if="loggedInUser">
        <button
          v-if="
            loggedInUser.subscription.code === 'plus_lite'
              ? canUserDownload(file.id) &&
                downloadLeftForplusLite > 0 &&
                downloadLeftForplusLite <= 3
              : canUserDownload(file.id)
          "
          title="Download this file"
          aria-label="Download this file"
          download
          rel="noindex, nofollow"
          @click.prevent="download(file)"
        >
          <svg class="icon icon-small" role="img" aria-hidden="true">
            <use :xlink:href="'/images/fa-icons.svg#' + iconNameFromFiletype(file.format)"></use>
          </svg>
          <span>
            {{ file.name }}
            <template v-if="!['slide', 'sheet'].includes(file.format)">
              ({{ file.format }})
            </template>
          </span>
          <span class="text-mute pull-right">
            <svg
              v-if="['slide', 'sheet'].includes(file.format)"
              class="icon icon-small"
              role="img"
              aria-hidden="true"
            >
              <use xlink:href="/images/fa-icons.svg?v=3#external-link"></use>
            </svg>
            <template v-else>{{ getFileSize(file.file_size) }}</template>
          </span>
        </button>
        <button
          v-else
          title="Upgrade to Download this file"
          aria-label="Upgrade to Download this file"
          class="download-disabled"
          @click="triggerSignUpEvent()"
        >
          <svg class="icon icon-small" role="img" aria-hidden="true">
            <use :xlink:href="'/images/fa-icons.svg#' + iconNameFromFiletype(file.format)"></use>
          </svg>
          <span>
            {{ file.name }}
            <template v-if="!['slide', 'sheet'].includes(file.format)">
              ({{ file.format }})
            </template>
          </span>
          <nuxt-link class="text-mute pull-right upgrade-link" :to="localePath('/plans/')">
            {{ 'Upgrade to Plus' }}
          </nuxt-link>
        </button>
      </template>
      <template v-else>
        <button
          title="Upgrade to Download this file"
          aria-label="Upgrade to Download this file"
          class="download-disabled"
        >
          <svg class="icon icon-small" role="img" aria-hidden="true">
            <use :xlink:href="'/images/fa-icons.svg#' + iconNameFromFiletype(file.format)"></use>
          </svg>
          <span>
            {{ file.name }}
            <template v-if="!['slide', 'sheet'].includes(file.format)">
              ({{ file.format }})
            </template>
          </span>
          <nuxt-link
            class="text-mute pull-right upgrade-link"
            :to="localePath('/plans/')"
            @click.native="triggerSignUpEvent()"
          >
            {{
              loggedInUser
                ? nextPlanforGuestUsers(file.id)
                  ? 'Sign Up to Free'
                  : 'Sign up to Plus'
                : resource.is_free
                  ? 'Sign Up to Free'
                  : 'Sign up to Plus'
            }}
          </nuxt-link>
        </button>
      </template>
    </li>
  </ul>
</template>

<script>
import { mapGetters } from 'vuex';
import { hasUserReachedDownloadLimit } from '~/utils/user';
import { isUnitOrLessonPlan } from '~/utils/resource';
import { getFirstItemFromQueryParam } from '~/utils/route';

export default {
  name: 'DownloadLink',
  props: {
    files: {
      type: Array,
      default: () => {
        return [];
      },
    },
    resourceType: { type: String, default: 'teaching-resource' },
    position: { type: String, default: 'Content | Select a Download' },
    clickType: { type: String, default: '' },
    clickText: { type: String, default: '' },
    resourceAvailability: { type: Boolean, default: false },
    customText: { type: String, default: '' },
    eventParams: {
      type: Object,
      default: () => {
        return {};
      },
    },
    pageCount: { type: Number, default: 0 },
  },
  data() {
    return {
      iconTypes: [
        { format: 'pdf', icon: 'file-pdf' },
        { format: 'docx', icon: 'file-word' },
        { format: 'dotx', icon: 'file-word' },
        { format: 'pptx', icon: 'file-powerpoint' },
        { format: 'potx', icon: 'file-powerpoint' },
        { format: 'slide', icon: 'google' },
        { format: 'sheet', icon: 'google' },
        { format: 'xlsx', icon: 'file-excel' },
        { format: 'xslm', icon: 'file-excel' },
        { format: 'mp3', icon: 'file-audio' },
        { format: 'mp4', icon: 'file-video' },
        { format: 'png', icon: 'image' },
        { format: 'jpg', icon: 'image' },
      ],
      isDownloading: false,
      downloadLeftForplusLite: 0,
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'loggedInUser', 'resource', 'userDownloadPermissions']),
  },
  watch: {
    loggedInUser(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.$store.dispatch('loadUserDownloadPermissions', this.files);
      }
    },
  },
  async mounted() {
    if (this.loggedInUser?.subscription.code === 'plus_lite') {
      await this.useValueChanged().then(response => {
        this.downloadLeftForplusLite = response.download_quota_counter;
      });
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.useValueChanged().then(value => {
            this.downloadLeftForplusLite = value.download_quota_counter;
          });
        }
      });
    }
  },
  methods: {
    getFileSize(size, units = 'mb') {
      if (!size || size === 0) {
        return '';
      }

      if (units === 'kb') {
        return Math.round(size / 1024) + ' kB';
      }

      const sizeInMB = size / (1024 * 1024);
      return sizeInMB < 0.1 ? sizeInMB.toFixed(2) + ' MB' : sizeInMB.toFixed(1) + ' MB';
    },
    triggerSignUpEvent() {
      let pageType = this.resource.type
        .trim()
        .replace(/[\s-]+/g, '_')
        .toLowerCase();
      let productAreas = '';
      if (this.$route.path.includes('teaching-resource')) {
        pageType = 'resource';
        productAreas = 'resources';
      } else if (this.$route.path.includes('unit-plan')) {
        pageType = 'unit_plan';
        productAreas = 'unit_plan';
      } else if (pageType === 'resource_pack') {
        pageType = 'pack';
        productAreas = 'pack';
      } else if (pageType === 'widget') {
        pageType = 'widget';
        productAreas = 'widget';
      } else if (pageType === 'lesson-plan') {
        pageType = 'lesson_plan';
        productAreas = 'lesson_plan';
      }

      let btnName = '';
      if (this.loggedInUser) {
        this.$mzAnalyticsApi({
          event: 'click',
          name: `${pageType}.download.upgrade_to_plus`,
          productArea: productAreas,
          entityType: pageType,
          entityId: this.resource.id,
        });
      } else {
        if (this.resource.is_free) {
          btnName = 'signup_to_free';
        } else {
          btnName = 'signup_to_plus';
        }
        this.$mzAnalyticsApi({
          event: 'click',
          name: `${pageType}.download.${btnName}`,
          productArea: productAreas,
          entityType: pageType,
          entityId: this.resource.id,
        });
      }
    },
    toggleUpgradeModal(show) {
      this.$store.dispatch('toggleUpgradeModal', show);
    },
    canUserDownload(fileId) {
      return this.userDownloadPermissions?.find(f => f.id === fileId)?.status === 200;
    },
    nextPlanforGuestUsers(fileId) {
      const filePermission = this.userDownloadPermissions?.find(f => f.id === fileId);
      return filePermission?.plans?.includes('free');
    },
    isDeskplate() {
      return (
        this.resource.links_to_studio &&
        this.resource.tags.find(({ name }) => name === 'Desk Plates' || name === 'Desk Name Plates')
      );
    },
    async download(file) {
      if (!file) {
        return;
      }
      this.isDownloading = true;
      this.$emit('toggleDownloadButton', this.isDownloading);
      if (this.isDeskplate()) {
        await this.$download(file.id, this.customText, this.pageCount, { output: 'pdf' }, true);
      } else {
        await this.$download(file.id, this.customText, this.pageCount);
      }
      let pageType = this.resource.type
        .trim()
        .replace(/[\s-]+/g, '_')
        .toLowerCase();
      let productAreas = '';
      if (pageType === 'teaching_resource') {
        pageType = 'resource';
        productAreas = 'resources';
      } else if (pageType === 'unit_plan') {
        pageType = 'unit_plan';
        productAreas = 'unit_plan';
      } else if (pageType === 'resource_pack') {
        pageType = 'pack';
        productAreas = 'pack';
      } else if (pageType === 'widget') {
        productAreas = 'widget';
      } else if (pageType === 'lesson_plan') {
        pageType = 'lesson_plan';
        productAreas = 'lesson_plan';
      }
      const analyticsName = `${pageType}.filedownloaded`;
      this.$mzAnalyticsApi({
        event: 'click',
        name: analyticsName,
        productArea: productAreas,
        entityType: pageType,
        entityId: this.resource.id,
      });
      this.eventFileDownloaded(file);
      const queryID = getFirstItemFromQueryParam(this.$route.query?.queryID);
      this.$sendAlgoliaConversion(String(this.resource.old_id), queryID);
      this.isDownloading = false;
      this.$emit('toggleDownloadButton', this.isDownloading);
    },
    iconNameFromFiletype(format) {
      // Map file type to an icon
      const iconType = this.iconTypes.find(item => item.format === format);
      return iconType ? iconType.icon : 'file';
    },
    handleUpgradeToDownloadClick(file) {
      this.eventUpgradeClicked();
      if (!this.loggedInUser) {
        this.$store.dispatch('modals/show', 'signUpCombined');
        return;
      }
      if (isUnitOrLessonPlan(this.resourceType)) {
        this.$store.dispatch('modals/show', 'unitPlanUpgrade');
        return;
      }
      if (file?.downloadable_file_type === 'editable') {
        this.$store.dispatch('modals/show', 'customizableResource');
        return;
      }
      if (hasUserReachedDownloadLimit(this.loggedInUser)) {
        this.$store.dispatch('modals/show', 'exhaustedDownloads');
        return;
      }
      if (!this.resource.is_free) {
        this.$store.dispatch('modals/show', 'premiumResource');
      }
    },
    async useValueChanged() {
      try {
        const response = await this.$axios.$get(
          '/public/v1/user/prefs?fields[]=download_quota_counter',
          {
            headers: {
              Authorization: this.$cookies.get('auth._token.local').replace('Bearer ', ''),
            },
          }
        );
        return response;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    eventUpgradeClicked() {
      const eventType = this.resource.event_params.Resource_Type_Name ? 'Resource' : 'Content';
      const upgradeParams = {
        Click_Text: 'Upgrade to download',
        Click_Type: 'Button Link',
        Click_Action: 'Send user to page | Plans and Pricing',
        Click_Page_Position: 'Content | Main CTA',
      };
    },
    eventFileDownloaded(file) {
      if (!this.resource.event_params) {
        return;
      }

      const eventType = this.resource.event_params.Resource_Type_Name ? 'Resource' : 'Content';
      const eventAction =
        file.format === 'slide' || file.format === 'sheet' ? 'Opened' : 'Downloaded'; // Exception for Google Slides or Sheets
      const eventParams = {
        Click_Type: this.clickType,
        Click_Text: this.clickText ? this.clickText : file.name + ' (' + file.format + ')',
        Click_Action: 'Download | Resource File',
        Click_Page_Position: this.position,
        User_Download_Quota_Left: this.loggedInUser.subscription?.quota_left || undefined,
      };

      const fileParams = {
        File_Format:
          file.format === 'slide'
            ? 'Google Slide'
            : file.format === 'sheet'
              ? 'Google Sheet'
              : file.format,
        File_Id: file.id,
        File_Name: file.name,
      };

      const fileParamsIntercom = {
        fileExtension: file.format,
        downloadType: 'Standard Download',
        file_id: file.id,
        file_name: file.name,
      };

      const fileDownloadIntercom = {
        ...this.resource.event_params_intercom,
        ...fileParamsIntercom,
        action_type_name: 'Download',
      };
      const resourceType = this.resource.is_free ? 'Free' : ' Premium';

      if (process.client && typeof window.gtag !== 'undefined') {
        window.gtag('event', 'resourceInteraction', {
          userUuid: this.loggedInUser.uuid,
          eventAction: 'Resource Interaction',
          resourceAvailability: resourceType,
          resourceName: this.resource.name,
          resourceId: this.resource.file_id,
        });
      }

      if (typeof this.$intercom !== 'undefined') {
        this.$intercom.trackEvent('Resource Downloaded', fileDownloadIntercom);
      }
    },
  },
};
</script>

<style scoped lang="scss">
a {
  color: inherit;
  padding: $base-padding;

  svg {
    height: 1.2rem;
    width: 1.2rem;
    margin-right: $base-padding;
  }

  .icon-small {
    height: 1rem;
    width: 1rem;
  }

  &:hover,
  &:focus {
    text-decoration: none;
  }

  &.active {
    font-weight: bold;
  }
}

.icon {
  flex: 0 0 auto;
}

.pull-right {
  white-space: nowrap;
}

.download-disabled {
  span {
    color: $grey-600;
    text-decoration: line-through;
  }
  .upgrade-link {
    color: $link-blue-500;
    text-decoration: none;
    &:hover,
    &:focus {
      color: $link-blue-700;
      text-decoration: underline;
    }
  }
}
</style>
